import { useField } from 'formik';

import { FormDialog } from 'components/dialog/FormDialog';
import { InlineSelectField } from 'components/select/InlineSelectField';
import type { IInlineSelectItem } from 'components/select/InlineSelect';
import { CustomError, ErrorCode } from 'src/error';

import { INVOICE_CHANNEL_OPTIONS, invoiceChannelsFromCustomer } from '../document-delivery';

export interface SendDocumentCustomer {
  emailEnabled: boolean;
  peppolEnabled: boolean;
  contacts: {
    id: number;
    name: string;
    email: string;
    shouldReceiveInvoices: boolean;
  }[];
}

export interface SendDocumentOptions {
  sendEmail: boolean;
  sendPeppol: boolean;
  contactIds?: number[];
}

interface SendDocumentChannelsDialogValues {
  channels: IInlineSelectItem[];
  contacts: IInlineSelectItem[];
}

const selectListClassName = 'pb-4';

const EmailRecipientsField: React.FC<{ items: IInlineSelectItem[] }> = ({ items }) => {
  const [field] = useField<IInlineSelectItem[]>({ name: 'channels' });
  const emailSelected = (field.value ?? []).some((item) => item.key === 'email');
  if (!emailSelected) {
    return null;
  }

  return (
    <InlineSelectField
      labelText="E-mail ontvangers"
      name="contacts"
      items={items}
      selectAllLabel="Alle contacten"
      helperText={items.length === 0 ? 'Deze klant heeft geen contacten met e-mailadres.' : undefined}
      className={selectListClassName}
    />
  );
};

export const SendDocumentChannelsDialog: React.FC<{
  triggerText: string;
  title: string;
  submitText: string;
  customer: SendDocumentCustomer;
  /** Set when the document already went out via Peppol: the channel is left out, this explains why. */
  peppolAlreadySent?: string;
  onSubmit: (options: SendDocumentOptions) => Promise<void>;
}> = ({ triggerText, title, submitText, customer, peppolAlreadySent, onSubmit }) => {
  const channelOptions = peppolAlreadySent
    ? INVOICE_CHANNEL_OPTIONS.filter((option) => option.key !== 'peppol')
    : INVOICE_CHANNEL_OPTIONS;
  const selectedChannels = invoiceChannelsFromCustomer(customer);
  const contactItems = customer.contacts
    .filter((contact) => contact.email)
    .map((contact) => ({
      key: String(contact.id),
      name: `${contact.name} - ${contact.email}`,
    }));
  const initialContacts = contactItems.filter((item) =>
    customer.contacts.some((contact) => String(contact.id) === item.key && contact.shouldReceiveInvoices),
  );

  return (
    <FormDialog<SendDocumentChannelsDialogValues>
      triggerText={triggerText}
      title={title}
      submitText={submitText}
      initialValues={{
        channels: channelOptions.filter((option) => selectedChannels.includes(option.key)),
        contacts: initialContacts,
      }}
      onSubmit={async (values) => {
        const channels = values.channels.map((item) => item.key);
        const sendEmail = channels.includes('email');
        const contactIds = values.contacts.map((item) => Number(item.key));
        if (sendEmail && contactIds.length === 0) {
          throw new CustomError('Selecteer minstens één e-mailontvanger', ErrorCode.VALIDATION_ERROR);
        }
        await onSubmit({
          sendEmail,
          sendPeppol: channels.includes('peppol'),
          contactIds: sendEmail ? contactIds : undefined,
        });
      }}
    >
      <div className="mb-10 flex flex-col gap-6">
        <InlineSelectField
          labelText="Verzendkanalen"
          name="channels"
          items={channelOptions}
          helperText={
            peppolAlreadySent
              ? `Altijd beschikbaar in het klantenportaal. ${peppolAlreadySent}`
              : 'Altijd beschikbaar in het klantenportaal.'
          }
          className={selectListClassName}
        />
        <EmailRecipientsField items={contactItems} />
      </div>
    </FormDialog>
  );
};
